import { useEffect } from "react";
import Slider from "react-slick";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Image } from "mui-image";
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { ProductInfo, Certificate } from "../../types";
import LinkButton from "../UI/LinkButton";
import CircularProgress from '@mui/material/CircularProgress';

// Image Slider Component
const ImageSlider = ({ images, loading }: { images: Array<{ src: string; alt: string; title?: string; label?: string }>, loading?: boolean }) => {
    const [showLoader, setShowLoader] = useState(true);

    useEffect(() => {
        let timer

        if (!loading) {
            timer = setTimeout(() => {
                setShowLoader(false);
            }, 300);
        } else {
            setShowLoader(true);
        }

        return () => clearTimeout(timer);
    }, [loading]);

    if (showLoader) {
        return (
            <Box sx={{ height: 280, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress sx={{ color: "#005bb5" }} />
            </Box>
        );
    }

    const imageSettings = {
        dots: true,
        infinite: images.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        adaptiveHeight: false,
    };



    return (
        <Box sx={{ width: "100%" }}>
            <Slider {...imageSettings}>
                {images.map((image, index) => (
                    <Box key={index} sx={{ px: 0.5 }}>
                        <Box sx={{ height: 280, width: 240, mx: "auto" }}>
                            <Image
                                src={image.src}
                                alt={image.alt}
                                title={image.title}
                                style={{
                                    objectFit: "contain",
                                    width: 240,
                                    height: 280,
                                }}
                                showLoading={false}
                                errorIcon={
                                    <Box sx={{
                                        height: 280,
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#f5f5f5",
                                        borderRadius: 1
                                    }}>
                                        <Typography variant="caption" color="gray">
                                            Failed to load image
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                textAlign: "center",
                                display: "block",
                                mt: 0.5,
                                fontSize: "0.7rem",
                            }}
                        >
                            {image.label}
                        </Typography>
                    </Box>
                ))}
            </Slider>
        </Box>
    );
};

interface ProductSliderProps {
    products: ProductInfo[];
    loading?: boolean;
    productImages?: Record<string, string | null>;
    eventImage?: string | null;
    productCertificates?: Array<{
        instanceId: string;
        productCertificates: Certificate[];
    }> | null;
    onProductCertificatesClick?: (certificates: Certificate[]) => void;
}

const ProductSlider = ({
    products,
    loading = true,
    productImages = {},
    eventImage = null,
    productCertificates = null,
    onProductCertificatesClick
}: ProductSliderProps) => {
    const normalizedProductCertificates = productCertificates ?? [];
    const [showDescription, setShowDescription] = useState(false);

    const settings = {
        dots: false,
        infinite: products.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        adaptiveHeight: false,
        centerMode: false,
    };

    if (products.length === 0) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                }}
            >
                <Typography variant="caption" color="gray">
                    No products for this event
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }} className="productSlider">
            <Slider {...settings}>
                {products.map((product, index) => {
                    const productCertInfo = normalizedProductCertificates.find(
                        cert => cert.instanceId === product.productInstanceId
                    );
                    const certificates = productCertInfo?.productCertificates || [];

                    const description = showDescription ? product.description : product.description.slice(0, 130);

                    // Prepare images for the slider: event image first, then product image
                    const images: Array<{ src: string; alt: string; title?: string; label?: string }> = [];

                    // Add event image if exists
                    if (eventImage) {
                        images.push({
                            src: eventImage,
                            alt: "Event Image",
                            title: "Event Image",
                            label: "Event Image"
                        });
                    }

                    // Add product image if exists
                    if (productImages[product.productInstanceId]) {
                        images.push({
                            src: productImages[product.productInstanceId]!,
                            alt: product.displayName || "Product Image",
                            title: product.displayName,
                            label: "Stock Image"
                        });
                    }

                    return (
                        <Box key={product.productInstanceId || index} sx={{ px: 1 }}>
                            {/* Image Slider */}
                            { }
                            <Box sx={{ mb: 2 }}>
                                <ImageSlider images={images} loading={loading} />
                            </Box>

                            {/* Product Details */}
                            <Box sx={{ textAlign: "center", px: 1 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: "bold",
                                        textTransform: "capitalize",
                                        mt: images?.length > 1 ? 3.5 : 0,
                                    }}
                                >
                                    {product.displayName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {description}
                                    {product.description.length > 130 && (
                                        <Typography
                                            variant="caption"
                                            color="#005bb5"
                                            sx={{ cursor: "pointer", display: "block" }}
                                            onClick={() => { setShowDescription(!showDescription) }}>
                                            {showDescription ? "Read less" : " Read more"}
                                        </Typography>
                                    )}
                                </Typography>

                                {/* Product Certificates Button */}
                                {certificates.length > 0 && (
                                    <Box>
                                        <LinkButton
                                            icon={<FileCopyOutlinedIcon />}
                                            tooltip="View Product Certificates"
                                            sx={{ mt: 1 }}
                                            onClick={() => onProductCertificatesClick?.(certificates)}
                                        >
                                            Product Certificates
                                        </LinkButton>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Slider>
        </Box>
    );
};

export default ProductSlider;
